import React from "react";
import classNames from "classnames";

import {
  ExplainAspect,
  useExplainAspect,
  useSetExplainAspect,
} from "features/Explain/WithExplainAspect";
import Tip from "ui/Tip";

export default function ExplainSelecAspect({
  label,
  showSubAspects,
}: {
  label: string;
  showSubAspects?: boolean;
}) {
  const [aspect, subAspect] = useExplainAspect();
  const setExplainAspect = useSetExplainAspect();

  function handleAspectChange(evt: React.ChangeEvent<HTMLInputElement>) {
    setExplainAspect(evt.currentTarget.value as ExplainAspect);
  }

  return (
    <div className="mb-2 flex flex-wrap gap-2 items-center max-w-full">
      <strong className="text-sm">
        {label}
        <Tip
          className="ml-1"
          content={
            <>
              <p>
                All costs are self-costs for each node (excluding children),
                except <b>Rows</b>. When loops are involved, all costs are a sum
                over all loops. When parallelism is involved, costs are summed
                over all workers (except <b>Runtime</b>, where only the slowest
                worker is reported, since the work happens in parallel).
              </p>
              <p>
                <b>Buffers</b> (blocks of data read during query execution) are
                counted per-access, not uniquely, so they will result in
                over-counting for plan nodes that repeatedly access the same
                data (especially nested loop joins). It can still be a useful
                comparison metric, but interpreting it requires caution. Buffer
                counts are a sum of shared and local blocks both hit and read
                from disk (or the OS page cache).
              </p>
            </>
          }
        />
        :
      </strong>
      <label className="font-semibold flex items-center gap-1 !mb-0">
        <input
          className="!m-0"
          type="radio"
          value="Est. Cost"
          checked={aspect == "Est. Cost"}
          onChange={handleAspectChange}
        />
        Est. Cost
      </label>
      <label className="font-semibold flex items-center gap-1 !mb-0">
        <input
          className="!m-0"
          type="radio"
          value="Runtime"
          checked={aspect == "Runtime"}
          onChange={handleAspectChange}
        />
        Runtime
      </label>
      <label className="font-semibold flex items-center gap-1 !mb-0">
        <input
          className="!m-0"
          type="radio"
          value="Rows"
          checked={aspect == "Rows"}
          onChange={handleAspectChange}
        />
        Rows
      </label>
      {showSubAspects && aspect === "Rows" && (
        <SubAspectToggle
          options={["Actual", "Estimated"]}
          value={subAspect}
          onChange={(v) => setExplainAspect(aspect, v)}
        />
      )}
      <label className="font-semibold flex items-center gap-1 !mb-0">
        <input
          className="!m-0"
          type="radio"
          value="Buffers"
          checked={aspect == "Buffers"}
          onChange={handleAspectChange}
        />
        Buffers
      </label>
      {showSubAspects && aspect === "Buffers" && (
        <SubAspectToggle
          options={["Hit + Read", "Dirtied"]}
          value={subAspect}
          onChange={(v) => setExplainAspect(aspect, v)}
        />
      )}
      <label className="font-semibold flex items-center gap-1 !mb-0">
        <input
          className="!m-0"
          type="radio"
          value="Reads"
          checked={aspect == "Reads"}
          onChange={handleAspectChange}
        />
        Reads
      </label>
      {showSubAspects && aspect === "Reads" && (
        <SubAspectToggle
          options={["Bytes", "Time"]}
          value={subAspect}
          onChange={(v) => setExplainAspect(aspect, v)}
        />
      )}
      <label className="font-semibold flex items-center gap-1 !mb-0">
        <input
          className="!m-0"
          type="radio"
          value="Writes"
          checked={aspect == "Writes"}
          onChange={handleAspectChange}
        />
        Writes
      </label>
      {showSubAspects && aspect === "Writes" && (
        <SubAspectToggle
          options={["Bytes", "Time"]}
          value={subAspect}
          onChange={(v) => setExplainAspect(aspect, v)}
        />
      )}
    </div>
  );
}

function SubAspectToggle<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly [T, T];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <span className="inline-flex rounded border border-gray-300 overflow-hidden text-xs leading-none">
      {options.map((opt, i) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={classNames(
            "!border-0 !rounded-none px-2 py-[3px]",
            i > 0 && "!border-l !border-gray-300",
            opt === value
              ? "!bg-gray-100 font-normal text-gray-900 cursor-default"
              : "!bg-white font-normal text-gray-600 hover:!bg-gray-50 hover:text-gray-900 cursor-pointer",
          )}
        >
          {opt}
        </button>
      ))}
    </span>
  );
}
